import { debug } from './console';

/**
 * When true, a performance report page has Color = SELECTIONS.
 * Global maneuver/chart GRADE hotkey listeners must not intercept digit keys so
 * {@link assignLocalPerformanceSelection} can run via AdvancedScatter.
 */
let performanceSelectionsHotkeysActive = false;

export function setPerformanceSelectionsHotkeysActive(active: boolean): void {
  performanceSelectionsHotkeysActive = active;
}

export function isPerformanceSelectionsHotkeysActive(): boolean {
  return performanceSelectionsHotkeysActive;
}

/** True when digit keys must not trigger maneuver/chart GRADE (performance Color = SELECTIONS). */
export function shouldBlockGradeLikeHotkeysForPerformanceSelections(key: string): boolean {
  return performanceSelectionsHotkeysActive && /^[0-9]$/.test(key);
}

/** Rows that carry `event_id` and optional client `selection` bucket. */
export type LocalSelectionRow = {
  event_id?: string | number;
  selection?: number;
};

export type LocalSelectionSignalPair<T extends LocalSelectionRow> = {
  get: () => T[];
  set: (rows: T[]) => void;
};

function normalizeEventId(raw: string | number | undefined): number | null {
  if (raw === undefined || raw === null || raw === '') return null;
  const n = typeof raw === 'string' ? parseInt(raw, 10) : Number(raw);
  return Number.isFinite(n) ? n : null;
}

/**
 * Mutates matching rows in place, then refreshes each touched signal with a new array
 * reference so Solid picks up changes. Returns number of row updates applied.
 */
export function assignLocalPerformanceSelection<T extends LocalSelectionRow>(
  eventIds: number[],
  value: number,
  aggregatePairs: Array<LocalSelectionSignalPair<T>>,
  cloudPairs?: Array<LocalSelectionSignalPair<T>>
): number {
  const idSet = new Set(eventIds.filter((id) => Number.isFinite(id)));
  let updates = 0;

  const touchRows = (rows: T[]): boolean => {
    let touched = false;
    for (const row of rows) {
      const eid = normalizeEventId(row.event_id);
      if (eid === null || !idSet.has(eid)) continue;
      if (row.selection !== value) {
        row.selection = value;
        updates += 1;
        touched = true;
      }
    }
    return touched;
  };

  for (const { get, set } of aggregatePairs) {
    const rows = get();
    if (!Array.isArray(rows) || rows.length === 0) continue;
    if (touchRows(rows)) set([...rows]);
  }

  if (cloudPairs) {
    for (const { get, set } of cloudPairs) {
      const rows = get();
      if (!Array.isArray(rows) || rows.length === 0) continue;
      if (touchRows(rows)) set([...rows]);
    }
  }

  debug('assignLocalPerformanceSelection', {
    eventCount: eventIds.length,
    value,
    rowUpdates: updates
  });

  return updates;
}
